import { Box, Button, Stack, Typography } from '@mui/material'
import * as monaco from 'monaco-editor'
import { useCallback, useState } from 'react'

import { RecipeTextEditor, useConfirm } from '@/components'
import {
  useGetCustomComponent,
  useUpdateCustomComponent,
} from '@/screens/ComponentLibrary/hooks'

import IModelDecoration = monaco.editor.IModelDecoration
import { parse, stringify } from 'yaml'

import { useComponentProvider } from '@/components/ViewComponentModal/ViewComponentProvider'
import { useSchema } from '@/hooks'
import { camelize, safeCypressID } from '@/utils'

export type SimpleApiConfig = {
  recipe: string
  name: string
}

type TextEditorState = {
  errors: IModelDecoration[]
  text: string
}

export const Editor = () => {
  const schema = useSchema({
    name: 'SimpleApiCallConfig',
    version: '1.0.0',
  })

  const { componentID } = useComponentProvider()
  const component = useGetCustomComponent<SimpleApiConfig>(componentID)
  const updateComponent = useUpdateCustomComponent<SimpleApiConfig>(componentID)

  const [simpleApiCall, setRecipe] = useState<TextEditorState>({
    errors: [],
    text: component.data?.settings?.recipe ?? '',
  })

  const confirm = useConfirm()
  const saveRecipe = async () => {
    try {
      console.log(simpleApiCall.errors)
      if (simpleApiCall.errors.length !== 0)
        await confirm({
          confirmationButtonProps: { color: 'primary', variant: 'contained' },
          content: (
            <>
              <Typography>
                {simpleApiCall.errors.length > 1
                  ? 'There are some errors in your simpleApiCall'
                  : 'There is an error in your simpleApiCall'}
              </Typography>
              <Stack>
                {simpleApiCall.errors.map((err) => (
                  <Typography key={err.id} variant="caption">
                    {/* @ts-ignore */}
                    {simpleApiCall.text.slice(err?.start - 1, err?.end)}
                  </Typography>
                ))}
              </Stack>
              <Typography>Are you sure that you want to save? </Typography>
            </>
          ),
          title: 'Save Simple Api call',
        })

      const addApiName = (config: string) => {
        const data = parse(config)

        return stringify({
          ...data,
          apiName: camelize(component.data?.name),
        })
      }
      await updateComponent.mutateAsync({
        id: componentID,
        settings: {
          recipe: addApiName(simpleApiCall.text),
        },
      })
    } catch (e) {
      console.log(e)
    }
  }

  const generateSchema = useCallback(
    (s: any) => ({
      ...s,
      properties: s.definitions.Data.properties,
      required: s.definitions.Data.required,
    }),
    []
  )

  const onChange = useCallback(
    ({ errors, text }: { text: string; errors: IModelDecoration[] }) => {
      setRecipe({ errors, text })
    },
    []
  )
  if (schema.isLoading) return null
  return (
    <Box sx={{ flex: 1, position: 'relative' }}>
      <Button
        id={safeCypressID('save_recipe_code')}
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          transform: 'translateY(-36px)',
        }}
        variant="contained"
        onClick={saveRecipe}
      >
        Save
      </Button>
      <RecipeTextEditor
        schema={generateSchema(schema.data)}
        value={simpleApiCall.text}
        onChange={onChange}
      />
    </Box>
  )
}
